import React, { useEffect, useState } from "react";
import { Stack, Box, Typography } from "@mui/material";
import Header from "../../layout/dashboard/header";
import EmploymentContent from "../../components/employment/EmploymentContent";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../../components/notification";
import {
  apiGetEmploymentsByFacility,
  apiGetEmploymentStats,
} from "../../api/employment";
import {
  dummyDataEmployeeStat,
  EmploymentDetailsProps,
  employmentStatsProps,
} from "../../interface/employment";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import useResponsive from "../../hooks/useResponsive";

const Employments: React.FC = () => {
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  const [employment_list, setEmployment_list] = useState<
    EmploymentDetailsProps[]
  >([]);
  const [filter_data, setFilter_data] = useState("");
  const [stats, setStats] = useState<employmentStatsProps>(
    dummyDataEmployeeStat,
  );

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First call stats
        await statsRequest.mutateAsync();
        // Then call jobs
        await employmentMutation.mutateAsync();
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state?.page, filter_data]);

  const statsRequest = useMutation({
    mutationFn: () => apiGetEmploymentStats(),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        // Handle stats data here
        setStats(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });

  const employmentMutation = useMutation({
    mutationFn: () =>
      apiGetEmploymentsByFacility(`${pagination_state?.page}`, filter_data),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setEmployment_list(data.data.data);
        } else {
          setEmployment_list([]);
        }
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: "#2F66F4",
              }}
            >
              Employment
            </Typography>
          </Stack>
        </Box>
      </Header>

      <EmploymentContent
        setFilterData={setFilter_data}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
        employment_list={employment_list}
        loading={employmentMutation.isPending}
        stats={stats}
      />
    </>
  );
};

export default Employments;
